import { f7ready } from 'framework7-react';
import {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import dayjs from 'dayjs';
import { Calendar } from 'framework7/types';
import { orderBy } from 'lodash';

import './InlineDatePicker.scss';

const hasValueChanged = (
  calendarRef: RefObject<Calendar.Calendar>,
  nextValue: Record<string, boolean>,
) => {
  const currentCalendarValue = calendarRef.current
    .getValue()
    .map((date) => dayjs(date).format('YYYY-MM-DD'));

  const updatedSelectedValues = Object.entries(nextValue).filter(([, isSelected]) => isSelected);

  if (currentCalendarValue.length !== updatedSelectedValues.length) {
    return true;
  }

  const isSame = currentCalendarValue.every((date) => nextValue[date]);

  return !isSame;
};

const getUpdatedDateRange = (unsortedValue: Date[], lastClickedDate: string) => {
  const sortedValue = orderBy(unsortedValue, (date) => dayjs(date).unix());
  let startDate = dayjs(sortedValue[0]);
  let endDate = sortedValue.length > 1 ? dayjs(sortedValue[sortedValue.length - 1]) : startDate;
  const clickedDate = dayjs(lastClickedDate);

  if (!clickedDate.isValid()) {
    // Bad input – just return the current range.
    return { [startDate.format('YYYY-MM-DD')]: true, [endDate.format('YYYY-MM-DD')]: true };
  }

  if (sortedValue.length > 1) {
    if (clickedDate.isSame(startDate, 'day') || clickedDate.isSame(endDate, 'day')) {
      // Clicking an endpoint keeps the range unchanged.
    } else if (clickedDate.isBefore(startDate, 'day')) {
      startDate = clickedDate; // extendDate backward
    } else if (clickedDate.isAfter(endDate, 'day')) {
      endDate = clickedDate; // extendDate forward
    } else {
      // Clicked inside the current range: move the nearest endpoint.
      const toStart = Math.abs(clickedDate.diff(startDate, 'day'));
      const toEnd = Math.abs(endDate.diff(clickedDate, 'day'));
      if (toStart < toEnd) startDate = clickedDate;
      else endDate = clickedDate; // ties go to endDate (matches your example)
    }
  }

  const s = startDate.format('YYYY-MM-DD');
  const e = endDate.format('YYYY-MM-DD');
  return { [s]: true, [e]: true };
};

export const InlineDatePicker: FC<{
  isRangePicker?: boolean;
  minDate?: string;
  value: Record<string, boolean>;
  onChange: Dispatch<SetStateAction<Record<string, boolean>>>; // (updatedValues: Record<string, boolean>) => void;
  onMonthYearChange?: (year: number, month: number) => void;
  events?: Calendar.DateRange;
}> = ({ isRangePicker, minDate, value = {}, onChange, onMonthYearChange, events }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<Calendar.Calendar>(undefined);
  const clickedDatesHistoryRef = useRef<string[]>([]);

  useEffect(() => {
    if (calendarRef.current) {
      if (hasValueChanged(calendarRef, value)) {
        const updatedValues = Object.entries(value)
          .filter(([, isSelected]) => isSelected)
          .map(([key]) => dayjs(key).toDate());
        calendarRef.current.setValue(updatedValues);
      }
    }
  }, [value]);

  useEffect(() => {
    if (divRef.current && !calendarRef.current) {
      f7ready((f7) => {
        if (!divRef.current) return;

        calendarRef.current = f7.calendar.create({
          containerEl: divRef.current,
          value: Object.entries(value)
            .filter(([, isSelected]) => isSelected)
            .map(([key]) => dayjs(key).toDate()),
          multiple: true,
          minDate: minDate ? dayjs(minDate).toDate() : undefined,
          events,
          firstDay: 0,
          rangePicker: isRangePicker,
          on: {
            monthYearChangeStart: (calendar) => {
              onMonthYearChange?.(calendar.currentYear, calendar.currentMonth);
            },
            dayClick: (calendar, dayEl, year, month, day) => {
              const date = dayjs(`${year}-${month + 1}-${day}`).format('YYYY-MM-DD');
              clickedDatesHistoryRef.current.push(date);

              if (!isRangePicker) {
                onChange((prevValue) => {
                  const updatedSelected = {
                    ...prevValue,
                    [date]: !prevValue[date],
                  };

                  return updatedSelected;
                });
              }
            },
            change: (calendar, unsortedValue: Date[]) => {
              if (isRangePicker) {
                const lastClickedDate = clickedDatesHistoryRef.current.pop();

                if (lastClickedDate) {
                  onChange(getUpdatedDateRange(unsortedValue, lastClickedDate));
                }
              }
            },
          },
        });
      });
    }
  });

  return <div ref={divRef} />;
};
